import { authStore, logout } from "@/store/authStore";
import { dialogStore } from "@/components/dialog/dialogStore";
import { useWebphoneStore } from "@/store/webphoneStore";
import { getIdleMs, getSessionIdleLimitMs, noteActivity, resetIdleClock } from "@/lib/idleClock";
import { subscribeActivity } from "@/lib/sessionChannel";
import { endSession } from "@/lib/authSession";
import { BRIDGE_EVENTS, bridge } from "@/lib/webphone-bridge/crossFrameworkBridge";
import { BrowserNotificationMessageType } from "@/components/browser-notifications/notification.types";

/**
 * The inactivity logout — the tenant's UI inactivity setting, 15 minutes by default (CT-372).
 *
 * This module owns the CUT. The server owns the truth — sip-idp refuses to refresh an idle
 * session — but a browser that has stopped talking to anything would otherwise sit on a painted
 * dashboard until the user came back, so the tab ends its own session on time and tells the rest.
 */

/** The warning dialog appears this long before the cut. */
export const IDLE_WARNING_LEAD_MS = 60 * 1000;
/** Wall-clock poll. Cheap, and the only thing that survives a suspended machine honestly. */
const IDLE_POLL_MS = 5 * 1000;

/** QA escape hatch (dev + stage BUILDS only): set e.g.
 *  `localStorage.setItem("__dev_idleLimitMs", "180000")` and reload for a 3-minute cut
 *  (warning at limit − 60s). Clamped to strictly shorter than the session's real limit, so it can
 *  only bring the cut forward, never stretch usable idle time. Production ignores it. */
const DEV_IDLE_LIMIT_STORAGE_KEY = "__dev_idleLimitMs";

// Gate on the BUILD MODE, not isDevOrStaging(): stage is served from app-stage.pbx.im, which
// matches none of that helper's host patterns, so the override was inert on the exact
// environment QA uses (review blocker on CT-36 QA-3).
function isIdleOverrideAllowed(): boolean {
    return import.meta.env.DEV || import.meta.env.MODE === "stage";
}

export function getIdleLimitMs(): number {
    const limitMs = getSessionIdleLimitMs();
    if (isIdleOverrideAllowed()) {
        try {
            const override = Number(localStorage.getItem(DEV_IDLE_LIMIT_STORAGE_KEY));
            if (Number.isFinite(override) && override > IDLE_WARNING_LEAD_MS && override < limitMs) {
                return override;
            }
        } catch {
            // Storage unavailable — run with the real limit.
        }
    }
    return limitMs;
}

/**
 * What counts as the user being here. Deliberately NOT mousemove: a nudged desk or a passing
 * cursor would hold a session open forever, which is the exact failure this feature exists to
 * prevent. Scroll and wheel are in because reading a call log is real work.
 */
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"] as const;

let pollTimer: ReturnType<typeof setInterval> | null = null;
/** Latch so a slow logout round-trip cannot start a second one on the next 5-second tick. */
let cutInProgress = false;
/** Edge-detect the warning so the browser notification fires once per idle stretch, not every
 *  5 seconds for a minute. */
let warningShown = false;

function setWarningVisible(visible: boolean): void {
    if (visible === warningShown) {
        return;
    }
    warningShown = visible;
    dialogStore.getState().setShowAutoLogOutWarning(visible);

    if (visible) {
        // Kept from the legacy socket path: users who minimised the tab still get told.
        bridge.emit(BRIDGE_EVENTS.BROWSER_NOTIFICATION, {
            type: BrowserNotificationMessageType.INACTIVITY_TIMEOUT,
            data: {},
        });
    }
}

/** The user is here: reset the window and take the warning down in the same call. Every path
 *  that proves presence goes through here — a local event, another tab, the dialog's button.
 *  Waiting for the 5-second poll instead read as a dead click (CT-36 QA round 5). */
export function confirmPresence(options: { broadcast?: boolean } = {}): void {
    noteActivity(options);
    setWarningVisible(false);
}

/** A ringing phone counts as much as a connected one — you cannot answer a call you were logged
 *  out of a second earlier. */
function isCallInProgress(): boolean {
    const { hasActiveCall, isRinging } = useWebphoneStore.getState();
    return hasActiveCall || isRinging;
}

function hasLiveSession(): boolean {
    return !!authStore.getState().authToken;
}

async function cutIdleSession(): Promise<void> {
    if (cutInProgress) {
        return;
    }
    cutInProgress = true;

    setWarningVisible(false);

    try {
        // The SAME teardown a menu logout runs, not just the transport call: hang the softphone
        // up, check the agent out of its queues, revoke the refresh cookie, drop the display
        // cookie and the chat DOM, then reload onto /login. Calling logoutSession() alone left
        // the agent checked in and the device registered, so queue calls kept being offered to a
        // browser sitting on the login form (CT-36 QA round 4). The reason rides the session-end
        // signal + the /login redirect so EVERY tab shows the inactivity banner itself (QA-3: a
        // shared persisted toast was consumed by whichever login page rendered first, leaving the
        // other tabs bannerless).
        await logout("inactivity");
    } catch {
        // Best effort — the cut must not depend on the network being up.
    }

    // Backstop for a logout() that threw before it could redirect.
    endSession({ reason: "inactivity" });
    // The latch is NOT released. This tab hard-navigates to /login, so the only correct next
    // state is "gone" — releasing it would let a slow logout round-trip be followed by a second
    // cut on the next poll.
}

function tick(): void {
    if (cutInProgress) {
        return;
    }

    if (!hasLiveSession()) {
        // Nothing to expire (login screen, or the session already ended some other way). Keep the
        // clock fresh so the next login does not start life part-way through the window.
        resetIdleClock();
        setWarningVisible(false);
        return;
    }

    if (isCallInProgress()) {
        noteActivity();
        setWarningVisible(false);
        return;
    }

    const idleMs = getIdleMs();
    const idleLimitMs = getIdleLimitMs();

    if (idleMs >= idleLimitMs) {
        void cutIdleSession();
        return;
    }

    setWarningVisible(idleMs >= idleLimitMs - IDLE_WARNING_LEAD_MS);
}

/**
 * Wire the idle policy to the browser. Called once from main.tsx next to initSessionSync().
 * Returns a teardown for tests.
 */
export function initIdleSession(): () => void {
    const onActivity = () => confirmPresence();
    for (const eventName of ACTIVITY_EVENTS) {
        // Capture + passive: activity must be counted even when a handler stops propagation, and
        // must never delay scrolling.
        document.addEventListener(eventName, onActivity, { capture: true, passive: true });
    }

    // Another tab reported the user is there. Do not echo it back.
    const unsubscribeActivity = subscribeActivity(() => confirmPresence({ broadcast: false }));

    pollTimer = setInterval(tick, IDLE_POLL_MS);

    return () => {
        for (const eventName of ACTIVITY_EVENTS) {
            document.removeEventListener(eventName, onActivity, { capture: true });
        }
        unsubscribeActivity();
        if (pollTimer !== null) {
            clearInterval(pollTimer);
            pollTimer = null;
        }
    };
}

/** Test-only: drop the cut latch between cases. */
export function resetIdleSessionForTests(): void {
    cutInProgress = false;
    warningShown = false;
    resetIdleClock();
}
